import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type VendorStatus = "completed" | "processing" | "waiting";

interface VendorProgress {
  id: number;
  name: string;
  status: VendorStatus;
}

const VENDORS = [
  { id: 1, name: "렌느브라이덜" },
  { id: 2, name: "셀레네 웨딩" },
  { id: 3, name: "위드브라이드" },
];

const RADIUS = 76;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function ReservationLoadingOverlay({
  isOpen = true,
  reservationCode,
  shops
}: {
  isOpen?: boolean;
  reservationCode: string;
  shops: { id: number; name: string }[];
  // onComplete?: () => void;
}) {
  
  const navigate = useNavigate()
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const canStartProgress = isOpen && reservationCode !== null;
  console.log(canStartProgress)
  useEffect(() => {
    if (!canStartProgress) {
      setStep(0);
      setElapsed(0);
      return;
    }

    const dotTimer = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    const stepTimer = setInterval(() => {
      setStep((prev) => {
        if (prev >= shops.length) {
          clearInterval(stepTimer);
          return prev;
        }
        return prev + 1;
      });
    }, 1200);

    return () => {
      clearInterval(dotTimer);
      clearInterval(stepTimer);
    };
  }, [isOpen, shops, canStartProgress]);

  // useEffect(() => {
  //   if (!isOpen) return;
  //   if (step !== shops.length) return;

  //   // const timer = setTimeout(() => {
  //   //   navigate({
  //   //     pathname: `/reservations/${reservationCode}`,
  //   //     search: `confirm=true`,
  //   //   });
  //   // }, 700);

  //   // return () => clearTimeout(timer);
  // }, [step, isOpen, reservationCode, navigate]);
  const onClickDone = () => {
    navigate({
      pathname: `/reservations/${reservationCode}`,
      search: `confirm=true`,
    });
  }
  const vendors: VendorProgress[] = useMemo(() => {
    return shops.map((vendor, index) => {
      if (!canStartProgress) {
        return { ...vendor, status: "waiting" };
      }
      if (index < step) {
        return { ...vendor, status: "completed" };
      }

      if (index === step) {
        return { ...vendor, status: "processing" };
      }

      return { ...vendor, status: "waiting" };
    });
  }, [canStartProgress,step, shops]);

  if (!isOpen) return null;

  const completed = canStartProgress ? vendors.filter((v) => v.status === "completed").length : 0;
  const total = vendors.length;
  const progress = (completed / total) * 100;
  const dashOffset = CIRCUMFERENCE - (progress / 100) * CIRCUMFERENCE;
  const dotCount = (elapsed % 4) + 1;
  const isDone = canStartProgress && completed === total;

  return (
    <div className="fixed inset-0 z-[9999] bg-[#F8F8F7] flex flex-col">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-5 pt-10 pb-4">
          <div className="text-center mb-8">
            <h1 className="text-lg font-bold text-[#111827] leading-snug">
              {isDone ? "예약이 완료되었습니다" : "예약을 확정하고 있습니다"}
            </h1>
            <p className="text-xs text-[#9CA3AF] mt-2 leading-relaxed px-4">
              {isDone
                ? "선택하신 업체의 예약이 모두 완료되었습니다."
                : `선택하신 업체의 예약 가능 여부를 순서대로 확인하고 있습니다${".".repeat(dotCount)}`}
            </p>
          </div>

          <div className="relative w-[176px] h-[176px] mx-auto mb-10">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 176 176">
              <circle
                cx="88"
                cy="88"
                r={RADIUS}
                fill="none"
                stroke="#E8E8E6"
                strokeWidth="8"
              />
              <circle
                cx="88"
                cy="88"
                r={RADIUS}
                fill="none"
                stroke={isDone ? "#10B981" : "#2563EB"}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
                style={{
                  transition:
                    "stroke-dashoffset 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />
            </svg>

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-[#1E3A8A] tabular-nums">
                {completed}
                <span className="text-lg font-medium text-[#9CA3AF] mx-0.5">
                  /
                </span>
                {total}
              </span>
              <span className="text-xs font-medium text-[#6B7280] mt-0.5">
                {isDone ? "예약 완료" : "예약 진행중"}
              </span>
            </div>
          </div>

          <div className="space-y-2.5 mb-6">
            {vendors.map((vendor, index) => (
              <VendorRow key={vendor.id} vendor={vendor} index={index} />
            ))}
          </div>

          <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl px-4 py-3.5">
            <div className="flex items-start gap-2.5">
              <div className="w-6 h-6 rounded-lg bg-[#2563EB] flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-notification-3-line text-white text-xs" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-[#374151] mb-1">
                  예약 완료 후 안내
                </p>
                <p className="text-[11px] text-[#6B7280] leading-relaxed">
                  {isDone
                    ? "예약이 잘 확정되었으니 업체에 다시 한번 확인하실 필요 없습니다"
                    : "모든 업체의 예약 확인이 완료되면 다음 단계로 이동합니다"}
                </p>
              </div>
            </div>
          </div>

          <div className="h-28" />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-[#E5E7EB] px-5 py-4 safe-area-bottom">
        <div className="max-w-lg mx-auto">
          <button
            disabled={!isDone}
            onClick={onClickDone}
            className={`w-full py-4 rounded-xl text-sm font-bold transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${
              isDone
                ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                : "bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
            }`}
          >
            {!isDone && (
              <i
                className="ri-loader-4-line text-base animate-spin"
                style={{ animationDuration: "1.2s" }}
              />
            )}

            {isDone
              ? "완료된 예약 상세 보기"
              : `예약 처리중${".".repeat(dotCount)}`}
          </button>
          <p className="text-center text-[10px] text-[#9CA3AF] mt-2">
            {isDone
              ? "예약이 잘 확정되었으니 업체에 다시 한번 확인하실 필요 없습니다"
              : "모든 업체의 예약 확인이 완료되면 다음 단계로 이동합니다"}
          </p>
        </div>
      </div>
    </div>
  );
}

function VendorRow({
  vendor,
  index,
}: {
  vendor: VendorProgress;
  index: number;
}) {
  const isProcessing = vendor.status === "processing";

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all ${
        isProcessing
          ? "bg-[#F8FAFF] border-[#DBEAFE] shadow-sm"
          : "bg-white border-[#EBEBEB]"
      }`}
      style={{ animationDelay: `${index * 0.12}s` }}
    >
      <StatusIcon status={vendor.status} />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#111827] truncate">
          {vendor.name}
        </p>
        <StatusText status={vendor.status} />
      </div>

      <StatusBadge status={vendor.status} />
    </div>
  );
}

function StatusIcon({ status }: { status: VendorStatus }) {
  if (status === "completed") {
    return (
      <div className="w-8 h-8 rounded-full bg-[#10B981] flex items-center justify-center flex-shrink-0">
        <i className="ri-check-line text-white text-sm" />
      </div>
    );
  }

  if (status === "processing") {
    return (
      <div className="w-8 h-8 rounded-full bg-[#2563EB] flex items-center justify-center flex-shrink-0 relative">
        <div className="absolute inset-0 rounded-full bg-[#2563EB]/20 animate-ping" />
        <i
          className="ri-loader-4-line text-white text-sm animate-spin"
          style={{ animationDuration: "1s" }}
        />
      </div>
    );
  }

  return (
    <div className="w-8 h-8 rounded-full border-2 border-[#D1D5DB] bg-white flex items-center justify-center flex-shrink-0">
      <span className="w-2 h-2 rounded-full bg-[#D1D5DB]" />
    </div>
  );
}

function StatusText({ status }: { status: VendorStatus }) {
  const labels = {
    completed: "예약 완료",
    processing: "예약 가능 여부 확인중",
    waiting: "대기중",
  };

  const colors = {
    completed: "text-[#059669]",
    processing: "text-[#2563EB]",
    waiting: "text-[#9CA3AF]",
  };

  return (
    <span className={`text-xs font-medium ${colors[status]}`}>
      {labels[status]}
    </span>
  );
}

function StatusBadge({ status }: { status: VendorStatus }) {
  if (status === "completed") {
    return (
      <span className="text-[11px] font-semibold text-[#059669] px-2 py-0.5 bg-[#ECFDF5] rounded-full whitespace-nowrap">
        완료
      </span>
    );
  }

  if (status === "processing") {
    return (
      <span className="text-[11px] font-semibold text-[#2563EB] px-2 py-0.5 bg-[#EFF6FF] rounded-full whitespace-nowrap">
        진행중
      </span>
    );
  }

  return (
    <span className="text-[11px] font-medium text-[#9CA3AF] px-2 py-0.5 bg-[#F9FAFB] rounded-full whitespace-nowrap">
      대기중
    </span>
  );
}